import {
    Children,
    DOMAttributes,
    FC,
    Fragment,
    MouseEvent,
    ReactElement,
    ReactNode,
    cloneElement,
    isValidElement,
    memo,
} from 'react';

type InterceptedProps = Pick<
    DOMAttributes<HTMLElement>,
    'onClick' | 'onPointerDown' | 'onPointerCancel' | 'onPointerLeave' | 'onPointerUp'
>;

const CLICK_DISTANCE_THRESHOLD_PX = 24;

const interceptClick = (node: ReactElement<InterceptedProps>) => {
    const props: InterceptedProps = {};

    if (node.props.onClick) {
        let click = false;
        let coordinates: { x: number; y: number } | null = null;
        props.onClick = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
        props.onPointerDown = (event) => {
            click = true;
            coordinates = { x: event.pageX, y: event.pageY };
            node.props.onPointerDown?.(event);
        };
        props.onPointerCancel = (event) => {
            click = false;
            node.props.onPointerCancel?.(event);
        };
        props.onPointerLeave = (event) => {
            click = false;
            node.props.onPointerLeave?.(event);
        };
        props.onPointerUp = (event) => {
            if (click) {
                if (
                    coordinates !== null &&
                    Math.pow(event.pageX - coordinates.x, 2) + Math.pow(event.pageY - coordinates.y, 2) <=
                        Math.pow(CLICK_DISTANCE_THRESHOLD_PX, 2)
                ) {
                    node.props.onClick?.(event as MouseEvent<HTMLElement>);
                }
                click = false;
                coordinates = null;
            }
            node.props.onPointerUp?.(event);
        };
    }

    return cloneElement(node, props);
};

const applyInterceptor = (node: ReactNode, func: (node: ReactElement) => ReactElement): ReactElement => {
    if (!isValidElement(node)) {
        return <Fragment>{node}</Fragment>;
    }

    let children;
    ((props) => {
        if (props.children) {
            children =
                props.children.length === 1
                    ? applyInterceptor(Children.only(props.children), func)
                    : Children.map(props.children, (child) => applyInterceptor(child, func));
        }
    })(node.props as { children: ReactNode[] });

    return cloneElement(func(node), {}, children);
};

export interface ClickInterceptorProps {
    children: ReactNode;
}

export const ClickInterceptor: FC<ClickInterceptorProps> = memo(({ children }) => {
    return applyInterceptor(children, interceptClick);
});

ClickInterceptor.displayName = 'ClickInterceptor';
